import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  Car, 
  Users, 
  Star, 
  MessageSquare,
  Plus,
  Settings,
  LogOut,
  BarChart3,
  Upload,
  Edit,
  Trash2,
  Phone,
  Mail,
  Calendar,
  X
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ServiceModal from './ServiceModal';
import TestimonialModal from './TestimonialModal';

// רשימת יצרני מכוניות (כולל רכבים חשמליים)
const CAR_MANUFACTURERS = [
  'Alfa Romeo',
  'Audi',
  'Bentley',
  'BMW',
  'BYD',
  'Canoo',
  'Chery',
  'Chevrolet',
  'Faraday Future',
  'Ferrari',
  'Fisker',
  'Ford',
  'Genesis',
  'Honda',
  'Hyundai',
  'Jaecoo',
  'Jaguar',
  'Jeep',
  'Kia',
  'Lamborghini',
  'Land Rover',
  'Lexus',
  'Li Auto',
  'Lordstown',
  'Lucid',
  'Maserati',
  'Mazda',
  'Mercedes-Benz',
  'Mini',
  'Mitsubishi',
  'NIO',
  'Nissan',
  'Peugeot',
  'Polestar',
  'Porsche',
  'Range Rover',
  'Renault',
  'Rivian',
  'Rolls-Royce',
  'Seat',
  'Skoda',
  'Subaru',
  'Suzuki',
  'Tesla',
  'Toyota',
  'Volkswagen',
  'Volvo',
  'Xpeng',
  'Zeekr'
];

// רשימת יצרני אופנועים
const MOTORCYCLE_MANUFACTURERS = [
  'Aprilia',
  'BMW Motorrad',
  'Benelli',
  'Ducati',
  'Harley-Davidson',
  'Honda',
  'Indian',
  'Kawasaki',
  'KTM',
  'Moto Guzzi',
  'MV Agusta',
  'Royal Enfield',
  'Suzuki',
  'Triumph',
  'Yamaha',
  'Zontes'
];

const AdminDashboard = ({ auth, onLogout }) => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [cars, setCars] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contentPages, setContentPages] = useState([]);
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [editingHero, setEditingHero] = useState(false);
  const [serviceModal, setServiceModal] = useState({ open: false, service: null, loading: false });
  const [testimonialModal, setTestimonialModal] = useState({ open: false, testimonial: null, loading: false });
  const [loading, setLoading] = useState(true);
  const [isAddCarOpen, setIsAddCarOpen] = useState(false);
  const [newCar, setNewCar] = useState({
    vehicleType: 'car', // 'car' or 'motorcycle'
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    listPrice: '',
    monthlyPayment: '',
    color: '',
    mileage: '',
    fuelType: 'בנזין',
    engine: '',
    transmission: 'אוטומטי',
    power: '',
    condition: 'משומש',
    hand: 'יד 1',
    description: '',
    images: ['']
  });
  const [uploadingImages, setUploadingImages] = useState(false);

  // Edit car state
  const [editingCar, setEditingCar] = useState(null);
  const [isEditCarOpen, setIsEditCarOpen] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

  const fetchWithAuth = async (endpoint, options = {}) => {
    console.log('fetchWithAuth called:', { endpoint, options, auth: auth ? 'present' : 'missing' });
    
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Basic ${auth}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Response data:', result);
    return result;
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsData = await fetchWithAuth('/admin/stats');
      setStats(statsData.stats);
      
      // Load cars
      const carsData = await fetchWithAuth('/admin/cars');
      setCars(carsData);
      
      // Load services
      const servicesData = await fetchWithAuth('/admin/services');
      setServices(servicesData);
      
      // Load testimonials
      const testimonialsData = await fetchWithAuth('/admin/testimonials');
      setTestimonials(testimonialsData);
      
      // Load inquiries
      const inquiriesData = await fetchWithAuth('/admin/inquiries');
      setInquiries(inquiriesData);

      // Load content pages
      const pagesData = await fetchWithAuth('/content/admin/pages');
      setContentPages(pagesData);

      // Load website settings
      const settingsData = await fetch(`${API_BASE}/settings/`);
      const settingsJson = await settingsData.json();
      setWebsiteSettings(settingsJson);

      // Load company info
      const companyData = await fetch(`${API_BASE}/company-info/`);
      const companyJson = await companyData.json();
      setCompanyInfo(companyJson);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "שגיאה בטעינת הנתונים",
        description: "לא ניתן לטעון את נתוני הדשבורד",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleAddCar = async () => {
    console.log('=== handleAddCar START ===');
    console.log('Current form state:', newCar);
    console.log('Loading state:', loading);
    
    // Prevent if already loading
    if (loading) {
      console.log('Already loading, preventing duplicate submission');
      return;
    }
    
    try {
      // Validate required fields
      if (!newCar.make || !newCar.model || !newCar.price) {
        console.error('❌ Validation failed:', { 
          make: newCar.make, 
          model: newCar.model, 
          price: newCar.price 
        });
        
        alert('שגיאה: אנא מלא את כל השדות הנדרשים (יצרן, דגם, מחיר)');
        
        toast({
          title: "שגיאה",
          description: "אנא מלא את כל השדות הנדרשים (יצרן, דגם, מחיר)",
          variant: "destructive",
          duration: 5000
        });
        return;
      }
      
      console.log('✓ Validation passed');

      const carData = {
        vehicleType: newCar.vehicleType || 'car',
        make: newCar.make,
        model: newCar.model,
        year: parseInt(newCar.year),
        price: parseInt(newCar.price),
        listPrice: newCar.listPrice ? parseInt(newCar.listPrice) : 0,
        monthlyPayment: newCar.monthlyPayment ? parseInt(newCar.monthlyPayment) : 0,
        mileage: parseInt(newCar.mileage) || 0,
        condition: newCar.condition,
        color: newCar.color,
        engine: newCar.engine,
        power: newCar.power,
        transmission: newCar.transmission,
        fuelType: newCar.fuelType,
        hand: newCar.hand || 'יד 1',
        images: newCar.images.filter(img => img.trim() !== ''),
        features: [],
        description: newCar.description,
        isActive: true
      };

      console.log('Sending car data:', carData);

      const response = await fetchWithAuth('/admin/cars', {
        method: 'POST',
        body: JSON.stringify(carData)
      });

      console.log('Car added successfully:', response);

      console.log('✓ Car added successfully!');
      
      alert('הצלחה! הרכב נוסף בהצלחה');
      
      toast({
        title: "הצלחה!",
        description: "הרכב נוסף בהצלחה",
        variant: "default",
        duration: 3000
      });

      // Reset form and close modal
      console.log('Resetting form...');
      setNewCar({
        vehicleType: 'car',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        price: '',
        listPrice: '',
        monthlyPayment: '',
        color: '',
        mileage: '',
        fuelType: 'בנזין',
        engine: '',
        transmission: 'אוטומטי',
        power: '',
        condition: 'משומש',
        hand: 'יד 1',
        description: '',
        images: ['']
      });
      
      console.log('Closing dialog...');
      setIsAddCarOpen(false);
      
      console.log('Reloading dashboard data...');
      loadDashboardData();
      
      console.log('=== handleAddCar COMPLETE ===');

    } catch (error) {
      console.error('❌ Error adding car:', error);
      
      alert('שגיאה: לא ניתן להוסיף את הרכב - ' + error.message);
      
      toast({
        title: "שגיאה",
        description: `לא ניתן להוסיף את הרכב: ${error.message}`,
        variant: "destructive",
        duration: 5000
      });
    } finally {
      console.log('=== handleAddCar END ===');
    }
  };

  const addImageField = () => {
    setNewCar(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const removeImageField = (index) => {
    setNewCar(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const updateImageField = (index, value) => {
    setNewCar(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const handleImageUpload = async (file, index) => {
    if (!file) return;

    console.log('Starting image upload:', { fileName: file.name, fileSize: file.size, fileType: file.type, index });
    setUploadingImages(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Uploading to:', `${API_BASE}/upload/car-image`);
      
      const response = await fetch(`${API_BASE}/upload/car-image`, {
        method: 'POST',
        body: formData
      });

      console.log('Upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Upload result:', result);
      
      // The backend returns base64 data URL directly - use it as is
      const imageUrl = result.image_url;
      
      console.log('Setting image URL:', imageUrl ? `${imageUrl.substring(0, 50)}...` : 'empty');
      
      // Update the image URL in the form using existing helper function
      updateImageField(index, imageUrl);

      toast({
        title: "✅ הצלחה!",
        description: "התמונה הועלתה בהצלחה",
        duration: 3000
      });

      console.log('Image upload completed successfully');

    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "❌ שגיאה",
        description: `שגיאה בהעלאת התמונה: ${error.message}`,
        variant: "destructive",
        duration: 5000
      });
    } finally {
      setUploadingImages(false);
      console.log('Upload process ended, loading state cleared');
    }
  };

  const handleDeleteCar = async (carId, carName) => {
    console.log('handleDeleteCar called:', { carId, carName });
    
    const confirmed = confirm(`האם אתה בטוח שברצונך למחוק את ${carName}?`);
    
    if (!confirmed) {
      console.log('Delete cancelled by user');
      return;
    }

    try {
      const response = await fetchWithAuth(`/admin/cars/${carId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: "הצלחה!",
          description: `${carName} נמחק בהצלחה`,
          variant: "default"
        });
        
        // Refresh cars list
        loadDashboardData();
      } else {
        throw new Error('Failed to delete car');
      }
    } catch (error) {
      console.error('Error deleting car:', error);
      toast({
        title: "שגיאה",
        description: "שגיאה במחיקת הרכב",
        variant: "destructive"
      });
    }
  };

  const handleEditCar = (car) => {
    console.log('handleEditCar called:', car);
    setEditingCar(car);
    setNewCar({
      make: car.make || '',
      model: car.model || '',
      year: car.year || new Date().getFullYear(),
      price: car.price?.toString() || '',
      listPrice: car.listPrice?.toString() || '',
      monthlyPayment: car.monthlyPayment?.toString() || '',
      mileage: car.mileage?.toString() || '',
      condition: car.condition || '',
      hand: car.hand || 'יד 1',
      color: car.color || '',
      engine: car.engine || '',
      power: car.power || '',
      transmission: car.transmission || '',
      fuelType: car.fuelType || '',
      images: car.images && car.images.length > 0 ? car.images : [''],
      description: car.description || ''
    });
    setIsEditCarOpen(true);
  };

  const handleUpdateCar = async () => {
    if (!editingCar) return;

    try {
      // Validate required fields
      if (!newCar.make || !newCar.model || !newCar.price) {
        toast({
          title: "שגיאה",
          description: "אנא מלא את כל השדות הנדרשים",
          variant: "destructive"
        });
        return;
      }

      // Prepare the data
      const carData = {
        vehicleType: newCar.vehicleType || 'car',
        make: newCar.make,
        model: newCar.model,
        year: parseInt(newCar.year),
        price: parseInt(newCar.price),
        listPrice: newCar.listPrice ? parseInt(newCar.listPrice) : 0,
        monthlyPayment: newCar.monthlyPayment ? parseInt(newCar.monthlyPayment) : 0,
        mileage: parseInt(newCar.mileage) || 0,
        condition: newCar.condition,
        hand: newCar.hand || 'יד 1',
        color: newCar.color,
        engine: newCar.engine,
        power: newCar.power,
        transmission: newCar.transmission,
        fuelType: newCar.fuelType,
        images: newCar.images.filter(img => img.trim() !== ''),
        features: [],
        description: newCar.description,
        isActive: true
      };

      console.log('Updating car:', editingCar.id, carData);

      const result = await fetchWithAuth(`/admin/cars/${editingCar.id}`, {
        method: 'PUT',
        body: JSON.stringify(carData)
      });

      console.log('✓ Car updated successfully:', result);
      
      alert('הצלחה! הרכב עודכן בהצלחה');
      
      toast({
        title: "הצלחה!",
        description: "הרכב עודכן בהצלחה",
        variant: "default",
        duration: 3000
      });

      // Reset form and close dialog
      setNewCar({
        vehicleType: 'car',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        price: '',
        listPrice: '',
        monthlyPayment: '',
        color: '',
        mileage: '',
        fuelType: 'בנזין',
        engine: '',
        transmission: 'אוטומטי',
        power: '',
        condition: 'משומש',
        hand: 'יד 1',
        description: '',
        images: ['']
      });
      setIsEditCarOpen(false);
      setEditingCar(null);
      
      // Refresh cars list
      loadDashboardData();
    } catch (error) {
      console.error('❌ Error updating car:', error);
      
      alert('שגיאה: לא ניתן לעדכן את הרכב - ' + error.message);
      
      toast({
        title: "שגיאה",
        description: error.message || "שגיאה בעדכון הרכב",
        variant: "destructive",
        duration: 5000
      });
    }
  };

  // Website Settings Handlers
  const handleUpdateWebsiteSettings = async () => {
    console.log('🎨 handleUpdateWebsiteSettings called!', websiteSettings);
    try {
      setEditingSettings(true);
      console.log('🔄 Sending settings update request...');
      
      const response = await fetchWithAuth('/settings/admin', {
        method: 'PUT',
        body: JSON.stringify(websiteSettings)
      });

      console.log('✅ Settings updated successfully:', response);
      setWebsiteSettings(response);
      
      toast({
        title: "הצלחה!",
        description: "הגדרות האתר עודכנו בהצלחה",
        variant: "default"
      });

    } catch (error) {
      console.error('❌ Error updating website settings:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן לעדכן את הגדרות האתר",
        variant: "destructive"
      });
    } finally {
      setEditingSettings(false);
    }
  };

  // Company Info Handlers
  const handleUpdateCompanyInfo = async () => {
    try {
      setEditingCompany(true);
      
      const response = await fetchWithAuth('/company-info/admin', {
        method: 'PUT',
        body: JSON.stringify(companyInfo)
      });

      setCompanyInfo(response);
      
      toast({
        title: "הצלחה!",
        description: "פרטי החברה עודכנו בהצלחה",
        variant: "default"
      });

    } catch (error) {
      console.error('Error updating company info:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן לעדכן את פרטי החברה",
        variant: "destructive"
      });
    } finally {
      setEditingCompany(false);
    }
  };

  // Hero Section Handler
  const handleUpdateHero = async () => {
    try {
      setEditingHero(true);
      
      const response = await fetchWithAuth('/company-info/admin', {
        method: 'PUT',
        body: JSON.stringify(companyInfo)
      });

      setCompanyInfo(response);
      
      toast({
        title: "הצלחה!",
        description: "Hero Section עודכן בהצלחה",
        variant: "default"
      });

    } catch (error) {
      console.error('Error updating hero section:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן לעדכן את Hero Section",
        variant: "destructive"
      });
    } finally {
      setEditingHero(false);
    }
  };

  // Services Handlers
  const handleSaveService = async (serviceData) => {
    try {
      setServiceModal(prev => ({ ...prev, loading: true }));

      let response;
      if (serviceModal.service) {
        // Edit existing service
        response = await fetchWithAuth(`/admin/services/${serviceModal.service.id}`, {
          method: 'PUT',
          body: JSON.stringify(serviceData)
        });
      } else {
        // Add new service
        response = await fetchWithAuth('/admin/services', {
          method: 'POST',
          body: JSON.stringify(serviceData)
        });
      }

      toast({
        title: "הצלחה!",
        description: serviceModal.service ? "השירות עודכן בהצלחה" : "השירות נוסף בהצלחה",
        variant: "default"
      });

      setServiceModal({ open: false, service: null, loading: false });
      loadDashboardData();

    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן לשמור את השירות",
        variant: "destructive"
      });
    } finally {
      setServiceModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteService = async (serviceId, serviceName) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את השירות "${serviceName}"?`)) {
      return;
    }

    try {
      await fetchWithAuth(`/admin/services/${serviceId}`, {
        method: 'DELETE'
      });

      toast({
        title: "הצלחה!",
        description: "השירות נמחק בהצלחה",
        variant: "default"
      });

      loadDashboardData();

    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן למחוק את השירות",
        variant: "destructive"
      });
    }
  };

  // Testimonials Handlers
  const handleSaveTestimonial = async (testimonialData) => {
    try {
      setTestimonialModal(prev => ({ ...prev, loading: true }));

      let response;
      if (testimonialModal.testimonial) {
        // Edit existing testimonial
        response = await fetchWithAuth(`/admin/testimonials/${testimonialModal.testimonial.id}`, {
          method: 'PUT',
          body: JSON.stringify(testimonialData)
        });
      } else {
        // Add new testimonial
        response = await fetchWithAuth('/admin/testimonials', {
          method: 'POST',
          body: JSON.stringify(testimonialData)
        });
      }

      toast({
        title: "הצלחה!",
        description: testimonialModal.testimonial ? "ההמלצה עודכנה בהצלחה" : "ההמלצה נוספה בהצלחה",
        variant: "default"
      });

      setTestimonialModal({ open: false, testimonial: null, loading: false });
      loadDashboardData();

    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן לשמור את ההמלצה",
        variant: "destructive"
      });
    } finally {
      setTestimonialModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteTestimonial = async (testimonialId, customerName) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את ההמלצה של ${customerName}?`)) {
      return;
    }

    try {
      await fetchWithAuth(`/admin/testimonials/${testimonialId}`, {
        method: 'DELETE'
      });

      toast({
        title: "הצלחה!",
        description: "ההמלצה נמחקה בהצלחה",
        variant: "default"
      });

      loadDashboardData();

    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן למחוק את ההמלצה",
        variant: "destructive"
      });
    }
  };

  // Inquiries Handler
  const handleUpdateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      console.log('📧 Updating inquiry status:', { inquiryId, newStatus });
      
      await fetchWithAuth(`/inquiries/admin/${inquiryId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      toast({
        title: "הצלחה!",
        description: "סטטוס הפנייה עודכן בהצלחה",
        variant: "default"
      });

      loadDashboardData();

    } catch (error) {
      console.error('Error updating inquiry status:', error);
      toast({
        title: "שגיאה",
        description: "לא ניתן לעדכן את סטטוס הפנייה",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">טוען...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/50 backdrop-blur-md border-b border-amber-500/20 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            {/* Admin Dashboard Logo */}
            <div className="relative">
              <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 w-10 h-10 rounded-xl shadow-lg flex items-center justify-center">
                <div className="text-black font-black text-lg">J</div>
              </div>
              {/* Admin indicator */}
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                A
              </div>
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-bold text-white">מערכת הניהול</h1>
              <div className="text-xs text-amber-400 font-medium">J CLASS MOTORS ADMIN</div>
            </div>
          </div>
          
          <Button
            onClick={onLogout}
            variant="outline"
            className="border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black"
          >
            <LogOut className="w-4 h-4 ml-2" />
            התנתק
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">רכבים פעילים</p>
                  <p className="text-3xl font-bold text-white">{stats?.cars || 0}</p>
                </div>
                <Car className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">שירותים</p>
                  <p className="text-3xl font-bold text-white">{stats?.services || 0}</p>
                </div>
                <Settings className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">המלצות</p>
                  <p className="text-3xl font-bold text-white">{stats?.testimonials || 0}</p>
                </div>
                <Star className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">פניות לקוחות</p>
                  <p className="text-3xl font-bold text-white">{stats?.inquiries || 0}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="cars" className="space-y-6">
          <TabsList className="bg-gray-900/50 border border-amber-500/20 w-full justify-start overflow-x-auto flex-nowrap">
            {/* Mobile: Horizontal scrollable tabs */}
            <TabsTrigger value="cars" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              🚗 רכבים
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              📝 תוכן
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              🔧 שירותים
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              ⭐ המלצות
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              💬 פניות
            </TabsTrigger>
            <TabsTrigger value="company" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              🏢 חברה
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black whitespace-nowrap px-4 py-2 text-sm">
              ⚙️ הגדרות
            </TabsTrigger>
          </TabsList>

          {/* Cars Tab */}
          <TabsContent value="cars">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">ניהול רכבים</CardTitle>
                  <Dialog open={isAddCarOpen} onOpenChange={setIsAddCarOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        onClick={() => {
                          setIsAddCarOpen(true);
                          setTimeout(() => {
                            document.body.style.pointerEvents = "";
                          }, 0);
                        }}
                      >
                        <Plus className="w-4 h-4 ml-2" />
                        הוסף רכב
                      </Button>
                    </DialogTrigger>
                    <DialogContent 
                      className="bg-gray-900 text-white border-amber-500/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
                      forceMount
                      onOpenAutoFocus={(e) => {
                        setTimeout(() => {
                          document.body.style.pointerEvents = "";
                        }, 0);
                      }}
                    >
                      <DialogHeader>
                        <DialogTitle className="text-amber-400">הוספת רכב חדש</DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4 mt-4">
                        {/* Vehicle Type Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-2">סוג רכב*</label>
                          
                          {/* Mobile: Native Select */}
                          <select
                            value={newCar.vehicleType}
                            onChange={(e) => setNewCar(prev => ({...prev, vehicleType: e.target.value, make: ''}))}
                            className="md:hidden w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                          >
                            <option value="car">🚗 מכונית</option>
                            <option value="motorcycle">🏍️ אופנוע</option>
                          </select>
                          
                          {/* Desktop: Radix Select */}
                          <div className="hidden md:block">
                            <Select 
                              value={newCar.vehicleType} 
                              onValueChange={(value) => setNewCar(prev => ({...prev, vehicleType: value, make: ''}))}
                            >
                              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                <SelectValue placeholder="בחר סוג רכב" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600">
                                <SelectItem value="car" className="text-white hover:bg-gray-700">
                                  🚗 מכונית
                                </SelectItem>
                                <SelectItem value="motorcycle" className="text-white hover:bg-gray-700">
                                  🏍️ אופנוע
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                                                <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">יצרן*</label>
                            
                            {/* Mobile: Native Select */}
                            <select
                              value={newCar.make}
                              onChange={(e) => setNewCar(prev => ({...prev, make: e.target.value}))}
                              className="md:hidden w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                            >
                              <option value="">בחר יצרן</option>
                              {(newCar.vehicleType === 'motorcycle' ? MOTORCYCLE_MANUFACTURERS : CAR_MANUFACTURERS).map(manufacturer => (
                                <option key={manufacturer} value={manufacturer}>
                                  {manufacturer}
                                </option>
                              ))}
                            </select>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.make} 
                                onValueChange={(value) => setNewCar(prev => ({...prev, make: value}))}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר יצרן" />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600 max-h-[300px]">
                                  {(newCar.vehicleType === 'motorcycle' ? MOTORCYCLE_MANUFACTURERS : CAR_MANUFACTURERS).map(manufacturer => (
                                    <SelectItem 
                                      key={manufacturer} 
                                      value={manufacturer}
                                      className="text-white hover:bg-gray-700"
                                    >
                                      {manufacturer}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">דגם*</label>
                            <Input
                              value={newCar.model}
                              onChange={(e) => setNewCar(prev => ({...prev, model: e.target.value}))}
                              placeholder="קורולה, X5, C-Class..."
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">שנה</label>
                            <Input
                              type="number"
                              value={newCar.year}
                              onChange={(e) => setNewCar(prev => ({...prev, year: parseInt(e.target.value)}))}
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">מחיר*</label>
                            <Input
                              type="number"
                              value={newCar.price}
                              onChange={(e) => setNewCar(prev => ({...prev, price: e.target.value}))}
                              placeholder="150000"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">מחירון (אופציונלי)</label>
                            <Input
                              type="number"
                              value={newCar.listPrice}
                              onChange={(e) => setNewCar(prev => ({...prev, listPrice: e.target.value}))}
                              placeholder="180000"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">החזר חודשי (אופציונלי)</label>
                            <Input
                              type="number"
                              value={newCar.monthlyPayment}
                              onChange={(e) => setNewCar(prev => ({...prev, monthlyPayment: e.target.value}))}
                              placeholder="2500"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">צבע</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.color}
                                onChange={(e) => setNewCar(prev => ({...prev, color: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="שחור">שחור</option>
                                <option value="לבן">לבן</option>
                                <option value="כסף">כסף</option>
                                <option value="אפור">אפור</option>
                                <option value="אדום">אדום</option>
                                <option value="כחול">כחול</option>
                                <option value="ירוק">ירוק</option>
                                <option value="צהוב">צהוב</option>
                                <option value="חום">חום</option>
                                <option value="זהב">זהב</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.color} 
                                onValueChange={(value) => {
                                  console.log('🎨 Color changed to:', value);
                                  setNewCar(prev => ({...prev, color: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר צבע..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="שחור" className="text-white">שחור</SelectItem>
                                  <SelectItem value="לבן" className="text-white">לבן</SelectItem>
                                  <SelectItem value="כסף" className="text-white">כסף</SelectItem>
                                  <SelectItem value="אפור" className="text-white">אפור</SelectItem>
                                  <SelectItem value="אדום" className="text-white">אדום</SelectItem>
                                  <SelectItem value="כחול" className="text-white">כחול</SelectItem>
                                  <SelectItem value="ירוק" className="text-white">ירוק</SelectItem>
                                  <SelectItem value="צהוב" className="text-white">צהוב</SelectItem>
                                  <SelectItem value="חום" className="text-white">חום</SelectItem>
                                  <SelectItem value="זהב" className="text-white">זהב</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">קילומטראז&apos;</label>
                            <Input
                              type="number"
                              value={newCar.mileage}
                              onChange={(e) => setNewCar(prev => ({...prev, mileage: e.target.value}))}
                              placeholder="50000"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">סוג דלק</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.fuelType}
                                onChange={(e) => setNewCar(prev => ({...prev, fuelType: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="בנזין">בנזין</option>
                                <option value="דיזל">דיזל</option>
                                <option value="היברידי">היברידי</option>
                                <option value="חשמלי">חשמלי</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.fuelType} 
                                onValueChange={(value) => {
                                  console.log('🚗 Fuel type changed to:', value);
                                  setNewCar(prev => ({...prev, fuelType: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר סוג דלק..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="בנזין" className="text-white">בנזין</SelectItem>
                                  <SelectItem value="דיזל" className="text-white">דיזל</SelectItem>
                                  <SelectItem value="היברידי" className="text-white">היברידי</SelectItem>
                                  <SelectItem value="חשמלי" className="text-white">חשמלי</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">מנוע</label>
                            <Input
                              value={newCar.engine}
                              onChange={(e) => setNewCar(prev => ({...prev, engine: e.target.value}))}
                              placeholder="2.0L 4 צילינדר"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">תיבת הילוכים</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.transmission}
                                onChange={(e) => setNewCar(prev => ({...prev, transmission: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="אוטומטי">אוטומטי</option>
                                <option value="ידני">ידני</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.transmission} 
                                onValueChange={(value) => {
                                  console.log('🚗 Transmission changed to:', value);
                                  setNewCar(prev => ({...prev, transmission: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר תיבת הילוכים..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="אוטומטי" className="text-white">אוטומטי</SelectItem>
                                  <SelectItem value="ידני" className="text-white">ידני</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">כוח סוס</label>
                            <Input
                              value={newCar.power}
                              onChange={(e) => setNewCar(prev => ({...prev, power: e.target.value}))}
                              placeholder="150 כ״ס"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">מצב</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.condition}
                                onChange={(e) => setNewCar(prev => ({...prev, condition: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="חדש">חדש</option>
                                <option value="משומש">משומש</option>
                                <option value="מחלף עמדה">מחלף עמדה</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.condition} 
                                onValueChange={(value) => {
                                  console.log('🚗 Condition changed to:', value);
                                  setNewCar(prev => ({...prev, condition: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר מצב..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="חדש" className="text-white">חדש</SelectItem>
                                  <SelectItem value="משומש" className="text-white">משומש</SelectItem>
                                  <SelectItem value="מחלף עמדה" className="text-white">מחלף עמדה</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">יד</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.hand}
                                onChange={(e) => setNewCar(prev => ({...prev, hand: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="יד 1">יד ראשונה</option>
                                <option value="יד 2">יד שנייה</option>
                                <option value="יד 3">יד שלישית</option>
                                <option value="יד 4">יד רביעית</option>
                                <option value="יד 5">יד חמישית</option>
                                <option value="יד 6+">יד שישית ומעלה</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.hand} 
                                onValueChange={(value) => {
                                  console.log('🖐️ Hand changed to:', value);
                                  setNewCar(prev => ({...prev, hand: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר יד..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="יד 1" className="text-white">יד ראשונה</SelectItem>
                                  <SelectItem value="יד 2" className="text-white">יד שנייה</SelectItem>
                                  <SelectItem value="יד 3" className="text-white">יד שלישית</SelectItem>
                                  <SelectItem value="יד 4" className="text-white">יד רביעית</SelectItem>
                                  <SelectItem value="יד 5" className="text-white">יד חמישית</SelectItem>
                                  <SelectItem value="יד 6+" className="text-white">יד שישית ומעלה</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">תיאור</label>
                          <Textarea
                            value={newCar.description}
                            onChange={(e) => setNewCar(prev => ({...prev, description: e.target.value}))}
                            placeholder="תיאור מפורט של הרכב..."
                            className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">תמונות</label>
                          <div className="space-y-3">
                            {newCar.images.map((image, index) => (
                              <div key={index} className="space-y-2">
                                {/* URL Input - Hidden on Mobile */}
                                <div className="hidden md:flex gap-2">
                                  <Input
                                    value={image}
                                    onChange={(e) => updateImageField(index, e.target.value)}
                                    placeholder="קישור לתמונה או העלה קובץ..."
                                    className="bg-gray-700 border-gray-600 text-white flex-1"
                                  />
                                  {newCar.images.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => removeImageField(index)}
                                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                
                                {/* File upload section */}
                                <div className="flex items-center gap-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleImageUpload(e.target.files[0], index);
                                      }
                                    }}
                                    className="hidden"
                                    id={`image-upload-${index}`}
                                  />
                                  <label
                                    htmlFor={`image-upload-${index}`}
                                    className="cursor-pointer bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded text-sm font-medium flex items-center gap-2 w-full md:w-auto justify-center"
                                  >
                                    <Upload className="w-4 h-4" />
                                    📷 בחר תמונה מהגלריה
                                  </label>
                                  {newCar.images.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => removeImageField(index)}
                                      className="md:hidden border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {uploadingImages && (
                                    <span className="text-amber-400 text-sm">מעלה...</span>
                                  )}
                                </div>
                                
                                {/* Image preview */}
                                {image && (
                                  <div className="relative">
                                    <img
                                      src={image}
                                      alt="תצוגה מקדימה"
                                      className="w-20 h-20 object-cover rounded border border-gray-600"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={addImageField}
                              className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black"
                            >
                              <Plus className="w-4 h-4 ml-2" />
                              הוסף תמונה
                            </Button>
                          </div>
                        </div>

                        {/* Spacer to ensure buttons are visible */}
                        <div className="h-4"></div>
                        
                        <div className="flex gap-4 pt-6 pb-20 bg-gray-800 -mx-6 px-6 border-t border-amber-500/20 mt-6" style={{ position: 'relative', zIndex: 10 }}>
                          <button
                            type="button"
                            onClick={() => {
                              alert('כפתור נלחץ!');
                              console.log('🔴 Button clicked!');
                              handleAddCar();
                            }}
                            disabled={loading}
                            style={{
                              width: '100%',
                              height: '56px',
                              backgroundColor: '#f59e0b',
                              color: 'black',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              pointerEvents: 'auto'
                            }}
                          >
                            {loading ? 'מוסיף...' : 'הוסף רכב'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              alert('ביטול נלחץ!');
                              console.log('🔵 Cancel clicked!');
                              setIsAddCarOpen(false);
                            }}
                            style={{
                              width: '100px',
                              height: '56px',
                              backgroundColor: 'transparent',
                              color: 'white',
                              fontSize: '16px',
                              border: '1px solid #9ca3af',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              pointerEvents: 'auto'
                            }}
                          >
                            ביטול
                          </button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Edit Car Dialog */}
                  <Dialog open={isEditCarOpen} onOpenChange={setIsEditCarOpen}>
                    <DialogContent 
                      className="bg-gray-900 text-white border-amber-500/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
                      forceMount
                    >
                      <DialogHeader>
                        <DialogTitle className="text-amber-400">עריכת רכב</DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4 mt-4">
                        {/* Vehicle Type Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-2">סוג רכב*</label>
                          
                          {/* Mobile: Native Select */}
                          <select
                            value={newCar.vehicleType}
                            onChange={(e) => setNewCar(prev => ({...prev, vehicleType: e.target.value, make: ''}))}
                            className="md:hidden w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                          >
                            <option value="car">🚗 מכונית</option>
                            <option value="motorcycle">🏍️ אופנוע</option>
                          </select>
                          
                          {/* Desktop: Radix Select */}
                          <div className="hidden md:block">
                            <Select 
                              value={newCar.vehicleType} 
                              onValueChange={(value) => setNewCar(prev => ({...prev, vehicleType: value, make: ''}))}
                            >
                              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                <SelectValue placeholder="בחר סוג רכב" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600">
                                <SelectItem value="car" className="text-white hover:bg-gray-700">
                                  🚗 מכונית
                                </SelectItem>
                                <SelectItem value="motorcycle" className="text-white hover:bg-gray-700">
                                  🏍️ אופנוע
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                                                <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">יצרן*</label>
                            
                            {/* Mobile: Native Select */}
                            <select
                              value={newCar.make}
                              onChange={(e) => setNewCar(prev => ({...prev, make: e.target.value}))}
                              className="md:hidden w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                            >
                              <option value="">בחר יצרן</option>
                              {(newCar.vehicleType === 'motorcycle' ? MOTORCYCLE_MANUFACTURERS : CAR_MANUFACTURERS).map(manufacturer => (
                                <option key={manufacturer} value={manufacturer}>
                                  {manufacturer}
                                </option>
                              ))}
                            </select>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.make} 
                                onValueChange={(value) => setNewCar(prev => ({...prev, make: value}))}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר יצרן" />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600 max-h-[300px]">
                                  {(newCar.vehicleType === 'motorcycle' ? MOTORCYCLE_MANUFACTURERS : CAR_MANUFACTURERS).map(manufacturer => (
                                    <SelectItem 
                                      key={manufacturer} 
                                      value={manufacturer}
                                      className="text-white hover:bg-gray-700"
                                    >
                                      {manufacturer}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">דגם*</label>
                            <Input
                              value={newCar.model}
                              onChange={(e) => setNewCar(prev => ({...prev, model: e.target.value}))}
                              placeholder="קורולה, X5, C-Class..."
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">שנה</label>
                            <Input
                              type="number"
                              value={newCar.year}
                              onChange={(e) => setNewCar(prev => ({...prev, year: parseInt(e.target.value)}))}
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">מחיר*</label>
                            <Input
                              type="number"
                              value={newCar.price}
                              onChange={(e) => setNewCar(prev => ({...prev, price: e.target.value}))}
                              placeholder="150000"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">מחירון (אופציונלי)</label>
                            <Input
                              type="number"
                              value={newCar.listPrice}
                              onChange={(e) => setNewCar(prev => ({...prev, listPrice: e.target.value}))}
                              placeholder="180000"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">החזר חודשי (אופציונלי)</label>
                            <Input
                              type="number"
                              value={newCar.monthlyPayment}
                              onChange={(e) => setNewCar(prev => ({...prev, monthlyPayment: e.target.value}))}
                              placeholder="2500"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">צבע</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.color}
                                onChange={(e) => setNewCar(prev => ({...prev, color: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="שחור">שחור</option>
                                <option value="לבן">לבן</option>
                                <option value="כסף">כסף</option>
                                <option value="אפור">אפור</option>
                                <option value="אדום">אדום</option>
                                <option value="כחול">כחול</option>
                                <option value="ירוק">ירוק</option>
                                <option value="צהוב">צהוב</option>
                                <option value="חום">חום</option>
                                <option value="זהב">זהב</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.color} 
                                onValueChange={(value) => {
                                  console.log('🎨 Color changed to:', value);
                                  setNewCar(prev => ({...prev, color: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר צבע..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="שחור" className="text-white">שחור</SelectItem>
                                  <SelectItem value="לבן" className="text-white">לבן</SelectItem>
                                  <SelectItem value="כסף" className="text-white">כסף</SelectItem>
                                  <SelectItem value="אפור" className="text-white">אפור</SelectItem>
                                  <SelectItem value="אדום" className="text-white">אדום</SelectItem>
                                  <SelectItem value="כחול" className="text-white">כחול</SelectItem>
                                  <SelectItem value="ירוק" className="text-white">ירוק</SelectItem>
                                  <SelectItem value="צהוב" className="text-white">צהוב</SelectItem>
                                  <SelectItem value="חום" className="text-white">חום</SelectItem>
                                  <SelectItem value="זהב" className="text-white">זהב</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">קילומטראז&apos;</label>
                            <Input
                              type="number"
                              value={newCar.mileage}
                              onChange={(e) => setNewCar(prev => ({...prev, mileage: e.target.value}))}
                              placeholder="50000"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">סוג דלק</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.fuelType}
                                onChange={(e) => setNewCar(prev => ({...prev, fuelType: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="בנזין">בנזין</option>
                                <option value="דיזל">דיזל</option>
                                <option value="היברידי">היברידי</option>
                                <option value="חשמלי">חשמלי</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.fuelType} 
                                onValueChange={(value) => {
                                  console.log('🚗 Fuel type changed to:', value);
                                  setNewCar(prev => ({...prev, fuelType: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר סוג דלק..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="בנזין" className="text-white">בנזין</SelectItem>
                                  <SelectItem value="דיזל" className="text-white">דיזל</SelectItem>
                                  <SelectItem value="היברידי" className="text-white">היברידי</SelectItem>
                                  <SelectItem value="חשמלי" className="text-white">חשמלי</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">מנוע</label>
                            <Input
                              value={newCar.engine}
                              onChange={(e) => setNewCar(prev => ({...prev, engine: e.target.value}))}
                              placeholder="2.0L 4 צילינדר"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">תיבת הילוכים</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.transmission}
                                onChange={(e) => setNewCar(prev => ({...prev, transmission: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="אוטומטי">אוטומטי</option>
                                <option value="ידני">ידני</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.transmission} 
                                onValueChange={(value) => {
                                  console.log('🚗 Transmission changed to:', value);
                                  setNewCar(prev => ({...prev, transmission: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר תיבת הילוכים..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="אוטומטי" className="text-white">אוטומטי</SelectItem>
                                  <SelectItem value="ידני" className="text-white">ידני</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">כוח סוס</label>
                            <Input
                              value={newCar.power}
                              onChange={(e) => setNewCar(prev => ({...prev, power: e.target.value}))}
                              placeholder="150 כ״ס"
                              className="bg-gray-700 border-gray-600 text-white"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">מצב</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.condition}
                                onChange={(e) => setNewCar(prev => ({...prev, condition: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="חדש">חדש</option>
                                <option value="משומש">משומש</option>
                                <option value="מחלף עמדה">מחלף עמדה</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.condition} 
                                onValueChange={(value) => {
                                  console.log('🚗 Condition changed to:', value);
                                  setNewCar(prev => ({...prev, condition: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר מצב..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="חדש" className="text-white">חדש</SelectItem>
                                  <SelectItem value="משומש" className="text-white">משומש</SelectItem>
                                  <SelectItem value="מחלף עמדה" className="text-white">מחלף עמדה</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">יד</label>
                            
                            {/* Mobile: Native Select */}
                            <div className="md:hidden">
                              <select
                                value={newCar.hand}
                                onChange={(e) => setNewCar(prev => ({...prev, hand: e.target.value}))}
                                className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-amber-500 focus:outline-none"
                              >
                                <option value="יד 1">יד ראשונה</option>
                                <option value="יד 2">יד שנייה</option>
                                <option value="יד 3">יד שלישית</option>
                                <option value="יד 4">יד רביעית</option>
                                <option value="יד 5">יד חמישית</option>
                                <option value="יד 6+">יד שישית ומעלה</option>
                              </select>
                            </div>
                            
                            {/* Desktop: Radix Select */}
                            <div className="hidden md:block">
                              <Select 
                                value={newCar.hand} 
                                onValueChange={(value) => {
                                  console.log('🖐️ Hand changed to:', value);
                                  setNewCar(prev => ({...prev, hand: value}));
                                }}
                              >
                                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                  <SelectValue placeholder="בחר יד..." />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-600">
                                  <SelectItem value="יד 1" className="text-white">יד ראשונה</SelectItem>
                                  <SelectItem value="יד 2" className="text-white">יד שנייה</SelectItem>
                                  <SelectItem value="יד 3" className="text-white">יד שלישית</SelectItem>
                                  <SelectItem value="יד 4" className="text-white">יד רביעית</SelectItem>
                                  <SelectItem value="יד 5" className="text-white">יד חמישית</SelectItem>
                                  <SelectItem value="יד 6+" className="text-white">יד שישית ומעלה</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">תיאור</label>
                          <Textarea
                            value={newCar.description}
                            onChange={(e) => setNewCar(prev => ({...prev, description: e.target.value}))}
                            placeholder="תיאור מפורט של הרכב..."
                            className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">תמונות</label>
                          <div className="space-y-3">
                            {newCar.images.map((image, index) => (
                              <div key={index} className="space-y-2">
                                {/* URL Input - Hidden on Mobile */}
                                <div className="hidden md:flex gap-2">
                                  <Input
                                    value={image}
                                    onChange={(e) => updateImageField(index, e.target.value)}
                                    placeholder="קישור לתמונה או העלה קובץ..."
                                    className="bg-gray-700 border-gray-600 text-white flex-1"
                                  />
                                  {newCar.images.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => removeImageField(index)}
                                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                
                                {/* File upload section */}
                                <div className="flex items-center gap-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleImageUpload(e.target.files[0], index);
                                      }
                                    }}
                                    className="hidden"
                                    id={`edit-image-upload-${index}`}
                                  />
                                  <label
                                    htmlFor={`edit-image-upload-${index}`}
                                    className="cursor-pointer bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded text-sm font-medium flex items-center gap-2 w-full md:w-auto justify-center"
                                  >
                                    <Upload className="w-4 h-4" />
                                    📷 בחר תמונה מהגלריה
                                  </label>
                                  {newCar.images.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => removeImageField(index)}
                                      className="md:hidden border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {uploadingImages && (
                                    <span className="text-amber-400 text-sm">מעלה...</span>
                                  )}
                                </div>
                                
                                {/* Image preview */}
                                {image && (
                                  <div className="relative">
                                    <img
                                      src={image}
                                      alt="תצוגה מקדימה"
                                      className="w-20 h-20 object-cover rounded border border-gray-600"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={addImageField}
                              className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black"
                            >
                              <Plus className="w-4 h-4 ml-2" />
                              הוסף תמונה
                            </Button>
                          </div>
                        </div>

                        {/* Spacer to ensure buttons are visible */}
                        <div className="h-4"></div>
                        
                        <div className="flex gap-4 pt-6 pb-20 sticky bottom-0 bg-gray-800 -mx-6 px-6 border-t border-amber-500/20 mt-6">
                          <Button
                            type="button"
                            onClick={handleUpdateCar}
                            className="bg-amber-500 hover:bg-amber-600 text-black flex-1 h-14 text-lg font-bold"
                            disabled={loading}
                          >
                            {loading ? 'מעדכן...' : 'עדכן רכב'}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => {
                              setIsEditCarOpen(false);
                              setEditingCar(null);
                            }}
                            variant="outline"
                            className="border-gray-600 text-white hover:bg-gray-700 h-14"
                          >
                            ביטול
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cars.map((car) => (
                    <div key={car.id} className="bg-gray-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        {car.images && car.images[0] && (
                          <img 
                            src={car.images[0]} 
                            alt={`${car.make} ${car.model}`}
                            className="w-16 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <h3 className="text-white font-bold">{car.make} {car.model}</h3>
                          <p className="text-gray-400">{car.year} • {formatPrice(car.price)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Badge variant={car.isActive ? "default" : "secondary"}>
                          {car.isActive ? "פעיל" : "לא פעיל"}
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Edit button clicked');
                            handleEditCar(car);
                          }}
                          className="text-blue-400 border-blue-400 hover:bg-blue-500 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-400 border-red-400 hover:bg-red-500 hover:text-white"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Delete button clicked with preventDefault');
                            handleDeleteCar(car.id, `${car.make} ${car.model}`);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Management Tab */}
          <TabsContent value="content">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">ניהול תוכן</CardTitle>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                    <Plus className="w-4 h-4 ml-2" />
                    הוסף עמוד
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Hero Section Management */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h3 className="text-white font-bold mb-3">עמוד הבית - Hero Section</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">כותרת ראשית</label>
                        <Input
                          value={companyInfo?.heroTitle || ''}
                          onChange={(e) => setCompanyInfo(prev => ({...prev, heroTitle: e.target.value}))}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="הרכב המושלם לכל אחד"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">כותרת משנה</label>
                        <Input
                          value={companyInfo?.heroSubtitle || ''}
                          onChange={(e) => setCompanyInfo(prev => ({...prev, heroSubtitle: e.target.value}))}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="רכבים לכל הסוגים ולכל הכיסים"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">תמונת רקע</label>
                        <Input
                          value={companyInfo?.heroImage || ''}
                          onChange={(e) => setCompanyInfo(prev => ({...prev, heroImage: e.target.value}))}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="קישור לתמונת רקע"
                        />
                      </div>
                      <Button 
                        size="sm" 
                        onClick={handleUpdateHero}
                        disabled={editingHero}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        {editingHero ? 'מעדכן...' : 'עדכן Hero Section'}
                      </Button>
                    </div>
                  </div>

                  {/* About Section Management */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h3 className="text-white font-bold mb-3">אודות החברה</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">טקסט אודות</label>
                        <Textarea
                          value={companyInfo?.aboutText || ''}
                          onChange={(e) => setCompanyInfo(prev => ({...prev, aboutText: e.target.value}))}
                          className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
                          placeholder="תיאור החברה..."
                        />
                      </div>
                      <Button 
                        size="sm" 
                        onClick={handleUpdateHero}
                        disabled={editingHero}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        {editingHero ? 'מעדכן...' : 'עדכן אודות'}
                      </Button>
                    </div>
                  </div>

                  {/* Custom Pages */}
                  <div className="bg-gray-800/50 rounded-lg p-4 md:col-span-2">
                    <h3 className="text-white font-bold mb-3">עמודים מותאמים אישית</h3>
                    <div className="space-y-2">
                      {contentPages.map((page) => (
                        <div key={page.id} className="flex items-center justify-between bg-gray-700/50 rounded p-3">
                          <div>
                            <h4 className="text-white font-medium">{page.title}</h4>
                            <p className="text-gray-400 text-sm">/{page.slug}</p>
                          </div>
                          <div className="flex gap-2">
                            <Badge variant={page.isPublished ? "default" : "secondary"}>
                              {page.isPublished ? "פורסם" : "טיוטה"}
                            </Badge>
                            <Button size="sm" variant="outline">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-400 border-red-400">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-white">פניות לקוחות</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="bg-gray-800/50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-white font-bold">{inquiry.name}</h3>
                          <p className="text-amber-400">{inquiry.subject}</p>
                        </div>
                        <div className="text-left">
                          <p className="text-gray-400 text-sm">{formatDate(inquiry.createdAt)}</p>
                          <Select 
                            value={inquiry.status} 
                            onValueChange={(value) => handleUpdateInquiryStatus(inquiry.id, value)}
                          >
                            <SelectTrigger className="w-32 mt-1 bg-gray-700 border-gray-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-600">
                              <SelectItem value="new" className="text-white">חדש</SelectItem>
                              <SelectItem value="contacted" className="text-white">נוצר קשר</SelectItem>
                              <SelectItem value="closed" className="text-white">סגור</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <p className="text-gray-300 mb-3">{inquiry.message}</p>
                      
                      <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-400">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 ml-1" />
                          <a href={`tel:${inquiry.phone}`} className="hover:text-amber-400">
                            {inquiry.phone}
                          </a>
                        </div>
                        {inquiry.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 ml-1" />
                            <a href={`mailto:${inquiry.email}`} className="hover:text-amber-400">
                              {inquiry.email}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center">
                          <span className="text-xs">
                            העדפת קשר: {inquiry.preferredContact === 'phone' ? 'טלפון' : 'אימייל'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">ניהול שירותים</CardTitle>
                  <Button 
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                    onClick={() => setServiceModal({ open: true, service: null, loading: false })}
                  >
                    <Plus className="w-4 h-4 ml-2" />
                    הוסף שירות
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {services.map((service) => (
                    <div key={service.id} className="bg-gray-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        <div className="w-16 h-16 bg-amber-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-2xl">{service.icon || '🔧'}</span>
                        </div>
                        <div>
                          <h3 className="text-white font-bold">{service.title}</h3>
                          <p className="text-gray-400 text-sm">{service.description?.substring(0, 60)}...</p>
                          {service.price && (
                            <p className="text-amber-400 text-sm">{service.price}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Badge variant={service.isActive ? "default" : "secondary"}>
                          {service.isActive ? "פעיל" : "לא פעיל"}
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setServiceModal({ open: true, service, loading: false })}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-400 border-red-400 hover:bg-red-500 hover:text-white"
                          onClick={() => handleDeleteService(service.id, service.title)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">ניהול המלצות</CardTitle>
                  <Button 
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                    onClick={() => setTestimonialModal({ open: true, testimonial: null, loading: false })}
                  >
                    <Plus className="w-4 h-4 ml-2" />
                    הוסף המלצה
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="bg-gray-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        {testimonial.image ? (
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name}
                            className="w-16 h-16 object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
                            <Users className="w-8 h-8 text-amber-400" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-white font-bold">{testimonial.name}</h3>
                          <p className="text-gray-400 text-sm">{testimonial.text?.substring(0, 60)}...</p>
                          <div className="flex items-center mt-1">
                            {[...Array(testimonial.rating || 5)].map((_, i) => (
                              <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                            ))}
                          </div>
                          {testimonial.location && (
                            <p className="text-gray-500 text-xs">{testimonial.location}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Badge variant={testimonial.isActive ? "default" : "secondary"}>
                          {testimonial.isActive ? "פעיל" : "לא פעיל"}
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setTestimonialModal({ open: true, testimonial, loading: false })}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-400 border-red-400 hover:bg-red-500 hover:text-white"
                          onClick={() => handleDeleteTestimonial(testimonial.id, testimonial.name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-white">פניות לקוחות</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="bg-gray-800/50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-white font-bold">{inquiry.name}</h3>
                          <p className="text-amber-400">{inquiry.subject}</p>
                        </div>
                        <div className="text-left">
                          <p className="text-gray-400 text-sm">{formatDate(inquiry.createdAt)}</p>
                          <Select 
                            value={inquiry.status} 
                            onValueChange={(value) => handleUpdateInquiryStatus(inquiry.id, value)}
                          >
                            <SelectTrigger className="w-32 mt-1 bg-gray-700 border-gray-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-600">
                              <SelectItem value="new" className="text-white">חדש</SelectItem>
                              <SelectItem value="contacted" className="text-white">נוצר קשר</SelectItem>
                              <SelectItem value="closed" className="text-white">סגור</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <p className="text-gray-300 mb-3">{inquiry.message}</p>
                      
                      <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-400">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 ml-1" />
                          <a href={`tel:${inquiry.phone}`} className="hover:text-amber-400">
                            {inquiry.phone}
                          </a>
                        </div>
                        {inquiry.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 ml-1" />
                            <a href={`mailto:${inquiry.email}`} className="hover:text-amber-400">
                              {inquiry.email}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center">
                          <span className="text-xs">
                            העדפת קשר: {inquiry.preferredContact === 'phone' ? 'טלפון' : 'אימייל'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-white">פרטי החברה</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">שם החברה</label>
                      <Input
                        value={companyInfo?.name || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, name: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">לוגו</label>
                      <Input
                        value={companyInfo?.logo || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, logo: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="קישור ללוגו"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">טלפון</label>
                      <Input
                        value={companyInfo?.phone || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, phone: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">וואטסאפ</label>
                      <Input
                        value={companyInfo?.whatsapp || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, whatsapp: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">אימייל</label>
                      <Input
                        value={companyInfo?.email || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, email: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">כתובת</label>
                      <Input
                        value={companyInfo?.address || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, address: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">שעות פעילות</label>
                      <Input
                        value={companyInfo?.workingHours || ''}
                        onChange={(e) => setCompanyInfo(prev => ({...prev, workingHours: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">פייסבוק</label>
                      <Input
                        value={companyInfo?.socialMedia?.facebook || ''}
                        onChange={(e) => setCompanyInfo(prev => ({
                          ...prev, 
                          socialMedia: {...(prev?.socialMedia || {}), facebook: e.target.value}
                        }))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">אינסטגרם</label>
                      <Input
                        value={companyInfo?.socialMedia?.instagram || ''}
                        onChange={(e) => setCompanyInfo(prev => ({
                          ...prev, 
                          socialMedia: {...(prev?.socialMedia || {}), instagram: e.target.value}
                        }))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">יוטיוב</label>
                      <Input
                        value={companyInfo?.socialMedia?.youtube || ''}
                        onChange={(e) => setCompanyInfo(prev => ({
                          ...prev, 
                          socialMedia: {...(prev?.socialMedia || {}), youtube: e.target.value}
                        }))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <Button 
                    onClick={handleUpdateCompanyInfo}
                    disabled={editingCompany}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {editingCompany ? 'שומר...' : 'עדכן פרטי החברה'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Website Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-gray-900/50 backdrop-blur-md border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-white">הגדרות האתר</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">הגדרות כלליות</h3>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">שם האתר</label>
                      <Input
                        value={websiteSettings?.siteName || ''}
                        onChange={(e) => setWebsiteSettings(prev => ({...prev, siteName: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">תיאור האתר</label>
                      <Textarea
                        value={websiteSettings?.siteDescription || ''}
                        onChange={(e) => setWebsiteSettings(prev => ({...prev, siteDescription: e.target.value}))}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">פונט</label>
                      <Select 
                        value={websiteSettings?.fontFamily || 'Inter'} 
                        onValueChange={(value) => setWebsiteSettings(prev => ({...prev, fontFamily: value}))}
                      >
                        <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          <SelectItem value="Inter" className="text-white">Inter</SelectItem>
                          <SelectItem value="Heebo" className="text-white">Heebo</SelectItem>
                          <SelectItem value="Assistant" className="text-white">Assistant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">צבעי האתר</h3>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">צבע ראשי</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={websiteSettings?.primaryColor || '#f59e0b'}
                          onChange={(e) => {
                            console.log('🎨 Primary color changed to:', e.target.value);
                            setWebsiteSettings(prev => ({...prev, primaryColor: e.target.value}));
                          }}
                          className="w-16 h-10 border border-gray-600 rounded cursor-pointer"
                        />
                        <Input
                          value={websiteSettings?.primaryColor || '#f59e0b'}
                          onChange={(e) => {
                            console.log('🎨 Primary color text changed to:', e.target.value);
                            setWebsiteSettings(prev => ({...prev, primaryColor: e.target.value}));
                          }}
                          className="bg-gray-700 border-gray-600 text-white flex-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">צבע משני</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={websiteSettings?.secondaryColor || '#000000'}
                          onChange={(e) => setWebsiteSettings(prev => ({...prev, secondaryColor: e.target.value}))}
                          className="w-16 h-10 border border-gray-600 rounded cursor-pointer"
                        />
                        <Input
                          value={websiteSettings?.secondaryColor || '#000000'}
                          onChange={(e) => setWebsiteSettings(prev => ({...prev, secondaryColor: e.target.value}))}
                          className="bg-gray-700 border-gray-600 text-white flex-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">צבע הדגשה</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={websiteSettings?.accentColor || '#ffffff'}
                          onChange={(e) => setWebsiteSettings(prev => ({...prev, accentColor: e.target.value}))}
                          className="w-16 h-10 border border-gray-600 rounded cursor-pointer"
                        />
                        <Input
                          value={websiteSettings?.accentColor || '#ffffff'}
                          onChange={(e) => setWebsiteSettings(prev => ({...prev, accentColor: e.target.value}))}
                          className="bg-gray-700 border-gray-600 text-white flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 space-y-4">
                  <h3 className="text-lg font-semibold text-white">CSS מותאם אישית</h3>
                  <Textarea
                    value={websiteSettings?.customCSS || ''}
                    onChange={(e) => setWebsiteSettings(prev => ({...prev, customCSS: e.target.value}))}
                    className="bg-gray-700 border-gray-600 text-white min-h-[200px] font-mono text-sm"
                    placeholder="/* CSS מותאם אישית */"
                  />
                </div>
                
                <div className="mt-6">
                  <Button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🔘 Save settings button clicked!');
                      handleUpdateWebsiteSettings();
                    }}
                    disabled={editingSettings}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {editingSettings ? 'שומר...' : 'שמור הגדרות'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      {/* Modals */}
      <ServiceModal
        isOpen={serviceModal.open}
        onClose={() => setServiceModal({ open: false, service: null, loading: false })}
        onSave={handleSaveService}
        service={serviceModal.service}
        loading={serviceModal.loading}
      />

      <TestimonialModal
        isOpen={testimonialModal.open}
        onClose={() => setTestimonialModal({ open: false, testimonial: null, loading: false })}
        onSave={handleSaveTestimonial}
        testimonial={testimonialModal.testimonial}
        loading={testimonialModal.loading}
      />
    </div>
  );
};

export default AdminDashboard;